import { useEffect, useMemo, useRef, useState } from 'react';
import { TaxonomyApi, AudienceCategoryItem, CountryItem, AudiencePlatformItem, ListAudiencesSortEnum, CategoryListResponseMeta } from '@digiseg-labs/api-client';

type UseAudiencesParams = {
  labels?: string[];
  country?: CountryItem | undefined;
  platform?: AudiencePlatformItem | undefined;
  filter?: string;
  pageafter?: string;
  pagesize?: number;
  sort?: ListAudiencesSortEnum;
};

export function useAudiences({  
  labels,
  country,
  platform,
  filter = '',
  pageafter,
  pagesize = 5,
  sort = 'asc', }: UseAudiencesParams = {}) {

  const [categories, setCategories] = useState<AudienceCategoryItem[]>([]);
  const [meta, setMeta] = useState<CategoryListResponseMeta | null>(null); 
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const countryCode = country ? country.code : '';
  const platformCode = platform ? platform.code : '';
  const labelsString = useMemo(() => labels?.join(','), [labels]);
  const previousQueryKeyRef = useRef<string | null>(null);
  const queryKey = useMemo(
    () => JSON.stringify({ countryCode, platformCode, pagesize, sort, filter, labels: labelsString ?? '' }),
    [countryCode, platformCode, pagesize, sort, filter, labelsString],
  );

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    
    const api = new TaxonomyApi();
    const effectivePageafter = previousQueryKeyRef.current === queryKey ? pageafter : undefined;
    previousQueryKeyRef.current = queryKey;
    
    api.listAudiences(platformCode || '', countryCode || '', pagesize, effectivePageafter, sort, filter, labelsString)
      .then(resp => {
        if (cancelled) return;
        if (resp.data.data && Array.isArray(resp.data.data) && resp.data.data.length > 0) {
          const firstItem = resp.data.data[0] as any;
          let newCategories: AudienceCategoryItem[] = [];
          if (firstItem.categories && Array.isArray(firstItem.categories)) {
            newCategories = (resp.data.data as any[]).flatMap((set: any) => set.categories || []);
          } else {
            newCategories = resp.data.data as AudienceCategoryItem[];
          }
          setCategories(prev => effectivePageafter ? [...prev, ...newCategories] : newCategories);
        } else {
          setCategories([]);
        }
        setMeta(resp.data.meta || null);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Error fetching audiences:', err);
        setError(err);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [platformCode, countryCode, pagesize, pageafter, sort, filter, labelsString, queryKey]);

  return {
    categories,
    meta,
    isLoading,
    error,
  };
}
